import { useCallback, useEffect, useRef, useState } from "react";
import { ConfirmButton } from "@/components/common/ConfirmButton";
import { TextInputField } from "@/components/common/TextInputField";

interface EntryNameDialogProps {
	open: boolean;
	title: string;
	label: string;
	confirmLabel: string;
	testIdPrefix: string;
	onConfirm: (name: string) => Promise<void>;
	onCancel: () => void;
}

/** 新しいファイルまたはフォルダの名前を入力するダイアログ。 */
export function EntryNameDialog({
	open,
	title,
	label,
	confirmLabel,
	testIdPrefix,
	onConfirm,
	onCancel,
}: EntryNameDialogProps) {
	const dialogRef = useRef<HTMLDialogElement>(null);
	const [name, setName] = useState("");
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		const el = dialogRef.current;
		if (!el) return;
		if (open && !el.open) {
			el.showModal();
			setName("");
			setLoading(false);
			setError(null);
		} else if (!open && el.open) {
			el.close();
		}
	}, [open]);

	const trimmed = name.trim();
	const hasInvalidChar = trimmed.includes("/");
	const canConfirm = trimmed.length > 0 && !hasInvalidChar && !loading;

	const handleConfirm = useCallback(async () => {
		if (!canConfirm) return;
		setLoading(true);
		setError(null);
		try {
			await onConfirm(trimmed);
		} catch (err) {
			setError(err instanceof Error ? err.message : "エラーが発生しました");
			setLoading(false);
		}
	}, [canConfirm, onConfirm, trimmed]);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === "Escape" && !loading) onCancel();
		},
		[onCancel, loading],
	);

	if (!open) return null;

	return (
		<dialog
			ref={dialogRef}
			onKeyDown={handleKeyDown}
			onCancel={(e) => {
				e.preventDefault();
				if (!loading) onCancel();
			}}
			onClick={(e) => {
				if (e.target === e.currentTarget && !loading) onCancel();
			}}
			className="fixed inset-0 z-50 m-auto w-[360px] rounded-4 border border-line-1 bg-surface-1 p-0 shadow-popover backdrop:bg-scrim"
			data-testid={testIdPrefix}
		>
			<form
				onSubmit={(e) => {
					e.preventDefault();
					void handleConfirm();
				}}
			>
				<div className="px-5 pt-5 pb-4">
					<h3 className="text-ui font-semibold text-fg-1">{title}</h3>
					<div className="mt-3">
						<TextInputField
							label={label}
							value={name}
							onChange={setName}
							error={error ?? (hasInvalidChar ? "「/」は使用できません。" : undefined)}
						/>
					</div>
				</div>
				<div className="flex justify-end gap-2 border-t border-line-1 px-5 py-3">
					<ConfirmButton
						variant="ghost"
						label="キャンセル"
						loading={false}
						size="sm"
						disabled={loading}
						onClick={onCancel}
						data-testid={`${testIdPrefix}-cancel`}
					/>
					<ConfirmButton
						label={confirmLabel}
						type="submit"
						loading={loading}
						disabled={!canConfirm}
						size="sm"
						data-testid={`${testIdPrefix}-confirm`}
					/>
				</div>
			</form>
		</dialog>
	);
}
